import {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  HttpStatusCode,
  InternalAxiosRequestConfig
} from 'axios';

import { AccessTokenResponse } from '@/app/network/models/access-token-response';

type RefreshTokenCoordinatorOptions = {
  refreshTokenFn: (refreshToken: string) => Promise<AccessTokenResponse>;
  getTokens: () => { refresh_token?: string } | null;
  setTokens: (tokens: AccessTokenResponse) => void;
  endUserSession: () => Promise<void>;
};

/**
 * A queueable request to replay when a token refresh succeeds or fails.
 */
type FailedRequests = {
  /**
   * Called when the request to be replayed is resolved after a token is refreshed.
   * @param value - The resolved response.
   */
  resolve: (response: AxiosResponse) => void;

  /**
   * Called when the request to be replayed is reject because the token refresh fails.
   * @param error - The error that caused the refresh to fail.
   */
  reject: (error: AxiosError) => void;

  /**
   * The original axios request configuration to be replayed.
   */
  config: AxiosRequestConfig;

  /**
   * The original error that caused the request queue to be created.
   */
  error: AxiosError;
};

export function createRefreshTokenCoordinator(options: RefreshTokenCoordinatorOptions) {
  let isTokenRefreshing = false;
  let failedRequests: FailedRequests[] = [];

  return {
    createErrorHandler(axiosInstance: AxiosInstance) {
      return async function (error: AxiosError) {
        const status = error.response?.status;
        const originalRequestConfig = error.config!;

        // if not a status related to auth, reject the promise
        if (status !== HttpStatusCode.Unauthorized && status !== HttpStatusCode.Forbidden) return Promise.reject(error);

        // if we are already refreshing the token store the request for replay when token is refreshed
        if (isTokenRefreshing) {
          return new Promise((resolve, reject) => {
            failedRequests.push({
              resolve,
              reject,
              config: originalRequestConfig,
              error
            });
          });
        }

        isTokenRefreshing = true; // start the token refresh process

        // attempt to refresh the token
        try {
          const { refresh_token } = options.getTokens() ?? {};

          // if there is no token route to login and reject the promise
          if (!refresh_token) {
            await options.endUserSession();

            failedRequests.forEach(({ reject, error }) => reject(error));

            return Promise.reject(error);
          }

          const response = await options.refreshTokenFn(refresh_token);

          options.setTokens(response);

          // token was successfully replay the requests and resolve
          failedRequests.forEach(({ resolve, reject, config }) => {
            axiosInstance(config)
              .then(resolve)
              .catch(reject);
          });
        } catch (refreshTokenError: unknown) { // attempt to refresh failed
          await options.endUserSession();

          return Promise.reject(error);
        } finally {
          failedRequests = []; // clear the queue
          isTokenRefreshing = false; // reset the loading state
        }

        return axiosInstance(originalRequestConfig); // replay the request with the refreshed token
      };
    }
  };
}

export function createAuthRequestInterceptor(getTokensFn: () => { access_token?: string } | null) {
  return async function (config: InternalAxiosRequestConfig) {
    const authTokens = getTokensFn();

    if (authTokens?.access_token) {
      config.headers.Authorization = `Bearer ${authTokens.access_token}`;
    }

    return config;
  };
}

export function unwrapResponseData(response: AxiosResponse) {
  return response.data;
}
